import React, { useState } from 'react';
import './RetestModal.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const RetestModal = ({ isOpen, onClose, retestWeek, currentPlan, onPlanUpdate }) => {
  const [formData, setFormData] = useState({
    retestDistance: '5k',
    retestTime: '',
    retestDate: new Date().toISOString().split('T')[0],
    workoutChoice: '' // 'original' or 'retest'
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showComparison, setShowComparison] = useState(false);
  const [comparisonData, setComparisonData] = useState(null);

  if (!isOpen) return null;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError(null);
  };

  const handleWorkoutChoice = (choice) => {
    setFormData(prev => ({
      ...prev,
      workoutChoice: choice
    }));
    setError(null);
  };

  const validateTime = (time) => {
    const timeRegex = /^(\d{1,2}):(\d{2})$|^(\d{1,2}):(\d{2}):(\d{2})$/;
    return timeRegex.test(time);
  };

  const handleSubmit = async () => {
    // If user chose original workout, just close modal
    if (formData.workoutChoice === 'original') {
      onClose();
      return;
    }

    // Validate retest data
    if (!formData.retestTime) {
      setError('Please enter your race time');
      return;
    }

    if (!validateTime(formData.retestTime)) {
      setError('Time must be in MM:SS or HH:MM:SS format (e.g., 22:30 or 1:45:00)');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/training-plans/update-from-retest`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          original_plan: currentPlan,
          retest_distance: formData.retestDistance,
          retest_time: formData.retestTime,
          retest_date: formData.retestDate,
          completed_week: retestWeek
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update training plan');
      }

      const data = await response.json();
      
      // Show comparison
      setComparisonData(data);
      setShowComparison(true);
      
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptUpdate = () => {
    if (comparisonData && onPlanUpdate) {
      onPlanUpdate(comparisonData.updated_plan);
    }
    onClose();
  };

  const formatRaceName = (race) => {
    const names = {
      '1500m': '1500m',
      '5k': '5K',
      '10k': '10K',
      'half_marathon': 'Half Marathon',
      'marathon': 'Marathon'
    };
    return names[race] || race;
  };

  return (
    <div className="retest-modal-overlay" onClick={onClose}>
      <div className="retest-modal" onClick={(e) => e.stopPropagation()}>
        {!showComparison ? (
          <>
            <div className="modal-header">
              <h2>🎯 Week {retestWeek} Retest</h2>
              <button className="close-btn" onClick={onClose}>×</button>
            </div>

            <div className="modal-body">
              <p className="modal-description">
                What did you do for your Saturday workout?
              </p>

              {/* Workout Choice */}
              <div className="workout-choice">
                <button
                  className={`choice-btn ${formData.workoutChoice === 'original' ? 'selected' : ''}`}
                  onClick={() => handleWorkoutChoice('original')}
                >
                  <div className="choice-icon">🏃</div>
                  <div className="choice-content">
                    <strong>Original Workout</strong>
                    <p>Stuck with the planned training run</p>
                  </div>
                </button>

                <button
                  className={`choice-btn ${formData.workoutChoice === 'retest' ? 'selected' : ''}`}
                  onClick={() => handleWorkoutChoice('retest')}
                >
                  <div className="choice-icon">⏱️</div>
                  <div className="choice-content">
                    <strong>Time Trial / Race</strong>
                    <p>Tested my fitness level</p>
                  </div>
                </button>
              </div>

              {/* Retest Form (only show if retest chosen) */}
              {formData.workoutChoice === 'retest' && (
                <div className="retest-form">
                  <h3>Enter Your Race Details</h3>

                  <div className="form-group">
                    <label htmlFor="retestDistance">Race Distance</label>
                    <select
                      id="retestDistance"
                      name="retestDistance"
                      value={formData.retestDistance}
                      onChange={handleInputChange}
                    >
                      <option value="1500m">1500m</option>
                      <option value="mile">Mile</option>
                      <option value="5k">5K</option>
                      <option value="10k">10K</option>
                      <option value="half_marathon">Half Marathon</option>
                      <option value="marathon">Marathon</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="retestTime">Your Time *</label>
                    <input
                      type="text"
                      id="retestTime"
                      name="retestTime"
                      value={formData.retestTime}
                      onChange={handleInputChange}
                      placeholder="e.g., 22:30 or 1:45:00"
                      autoFocus
                    />
                    <small>Format: MM:SS or HH:MM:SS</small>
                  </div>

                  <div className="form-group">
                    <label htmlFor="retestDate">Date</label>
                    <input
                      type="date"
                      id="retestDate"
                      name="retestDate"
                      value={formData.retestDate}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
              )}

              {error && (
                <div className="error-message">
                  <strong>⚠️ {error}</strong>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button
                className="btn-secondary"
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={handleSubmit}
                disabled={loading || !formData.workoutChoice}
              >
                {loading ? 'Processing...' : formData.workoutChoice === 'original' ? 'Done' : 'Update My Plan →'}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="modal-header">
              <h2>🎉 Your Plan Has Been Updated!</h2>
              <button className="close-btn" onClick={handleAcceptUpdate}>×</button>
            </div>

            <div className="modal-body">
              {/* Fitness Level Improvement */}
              <div className="comparison-section">
                <h3>Fitness Level Improvement</h3>
                <div className="vdot-comparison">
                  <div className="vdot-improvement">
                    <span className="improvement-value">
                      {comparisonData.vdot_comparison.improvement > 0 ? '+' : ''}{comparisonData.vdot_comparison.improvement_percentage}% improvement
                    </span>
                  </div>
                </div>
              </div>

              {/* Pace Comparison */}
              <div className="comparison-section">
                <h3>Updated Training Paces</h3>
                <div className="pace-comparison-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Pace Type</th>
                        <th>Old</th>
                        <th>New</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td><strong>Easy</strong></td>
                        <td>{comparisonData.pace_comparison.old_paces.easy}</td>
                        <td className="new-pace">{comparisonData.pace_comparison.new_paces.easy}</td>
                      </tr>
                      <tr>
                        <td><strong>Marathon</strong></td>
                        <td>{comparisonData.pace_comparison.old_paces.marathon}</td>
                        <td className="new-pace">{comparisonData.pace_comparison.new_paces.marathon}</td>
                      </tr>
                      <tr>
                        <td><strong>Threshold</strong></td>
                        <td>{comparisonData.pace_comparison.old_paces.threshold}</td>
                        <td className="new-pace">{comparisonData.pace_comparison.new_paces.threshold}</td>
                      </tr>
                      <tr>
                        <td><strong>Interval</strong></td>
                        <td>{comparisonData.pace_comparison.old_paces.interval}</td>
                        <td className="new-pace">{comparisonData.pace_comparison.new_paces.interval}</td>
                      </tr>
                      <tr>
                        <td><strong>Repetition</strong></td>
                        <td>{comparisonData.pace_comparison.old_paces.repetition}</td>
                        <td className="new-pace">{comparisonData.pace_comparison.new_paces.repetition}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="info-box">
                <strong>✨ What's Updated:</strong>
                <p>• All remaining {comparisonData.weeks_remaining} weeks have new paces</p>
                <p>• Your training will now match your current fitness</p>
                <p>• Keep up the great work!</p>
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="btn-primary btn-large"
                onClick={handleAcceptUpdate}
              >
                Continue with Updated Plan →
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default RetestModal;
